/**
 * Admin: Sidebar-Labels, Sichtbarkeit + Gruppe pro Tenant (2026-08-17, Gruppen 2026-09-17).
 *
 * Universeller Slug-Override-Editor fuer die Portal-Sidebar. Zeigt jede
 * bekannte Sidebar-Zeile (Modul-Toplevel, Sub-Eintraege wie Berichte/Aufgaben,
 * dynamisch registrierte Module aus /portal/modules/available, System-Items
 * wie Startseite/Kalender/Todos) und laesst pro Zeile den Anzeige-Text
 * ueberschreiben, die Zeile fuer den Tenant komplett ausblenden oder die
 * Arbeitsbereich-Gruppe waehlen (Joerg 2026-09-17).
 *
 * Datenmodell: portal_sidebar_label_overrides (tenant_id, slug, alias, hidden, section).
 * Kein Override = Standard aus NAV_ITEMS / Modul-Registry.
 *
 * Master-Liste kommt vom Client: NAV_ITEMS (statische, hardcoded Items) +
 * /portal/modules/available (dynamisch registrierte Container). Der Server
 * kennt die Master-Liste nicht — er speichert nur was abweicht.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Info, Eye, EyeOff } from 'lucide-react';
import { apiFetch } from '@onvice/portal-shell-api';

import { NAV_ITEMS } from '../../components/PortalSidebar';
import {
  SIDEBAR_GROUPS, groupDef, isSidebarGroupSlug, mapNavSectionToGroup, resolveGroup,
  type SidebarGroupSlug, type SidebarOverride,
} from '../../lib/sidebar-groups';

interface MasterItem {
  slug: string;
  defaultLabel: string;
  defaultGroup: SidebarGroupSlug;
  source: 'static' | 'dynamic';
}

interface AvailableModuleResponse {
  slug: string;
  name: string;
  nav: { section: string; label: string } | null;
}

type OverridePatch = { alias?: string | null; hidden?: boolean; section?: string | null };

const NO_OVERRIDE: SidebarOverride = { alias: null, hidden: false, section: null };

export default function AdminSidebarLabelsPage() {
  const [dynamicItems, setDynamicItems] = useState<MasterItem[]>([]);
  const [overrides, setOverrides] = useState<Record<string, SidebarOverride>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function loadAll(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const [ovRes, modRes] = await Promise.all([
        apiFetch<{ success: boolean; data: Record<string, Partial<SidebarOverride>> }>('/admin/sidebar-overrides'),
        apiFetch<{ success: boolean; data: AvailableModuleResponse[] }>('/portal/modules/available'),
      ]);
      const map: Record<string, SidebarOverride> = {};
      for (const [slug, ov] of Object.entries(ovRes.data ?? {})) {
        map[slug] = { alias: ov.alias ?? null, hidden: !!ov.hidden, section: ov.section ?? null };
      }
      setOverrides(map);
      // Dynamische Module (die nicht schon in NAV_ITEMS stehen) als MasterItems.
      const staticSlugs = new Set(NAV_ITEMS.map((it) => it.slug));
      const dyn: MasterItem[] = (modRes.data ?? [])
        .filter((m) => m.nav !== null && !staticSlugs.has(m.slug))
        .map((m) => ({
          slug: m.slug,
          defaultLabel: m.nav!.label || m.name,
          defaultGroup: mapNavSectionToGroup(m.nav!.section, m.slug),
          source: 'dynamic' as const,
        }));
      setDynamicItems(dyn);
      setDrafts({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void loadAll(); }, []);

  // Master-Liste (statisch + dynamisch), Reihenfolge folgt NAV_ITEMS.
  const master: MasterItem[] = useMemo(() => {
    const staticItems: MasterItem[] = NAV_ITEMS.map((it) => ({
      slug: it.slug,
      defaultLabel: it.label,
      defaultGroup: it.group,
      source: 'static' as const,
    }));
    return [...staticItems, ...dynamicItems];
  }, [dynamicItems]);

  // Gruppierung nach EFFEKTIVER Gruppe (Override > Default), Reihenfolge SIDEBAR_GROUPS.
  const grouped = useMemo(() => {
    const out = new Map<SidebarGroupSlug, MasterItem[]>();
    for (const g of SIDEBAR_GROUPS) out.set(g.slug, []);
    for (const m of master) {
      const eff = resolveGroup(m.defaultGroup, overrides[m.slug]?.section);
      out.get(eff)!.push(m);
    }
    return out;
  }, [master, overrides]);

  async function patchOverride(slug: string, patch: OverridePatch): Promise<void> {
    setSavingSlug(slug);
    setMessage(null);
    try {
      const res = await apiFetch<{ success: boolean; data: SidebarOverride & { slug: string } }>(
        `/admin/sidebar-overrides/${encodeURIComponent(slug)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      );
      // Server-Antwort als Wahrheit uebernehmen.
      setOverrides((prev) => {
        const next = { ...prev };
        const isDefault = res.data.alias === null && res.data.hidden === false && (res.data.section ?? null) === null;
        if (isDefault) delete next[slug];
        else next[slug] = { alias: res.data.alias, hidden: res.data.hidden, section: res.data.section ?? null };
        return next;
      });
      // Draft fuer diesen Slug wegwerfen (falls Alias gespeichert wurde).
      if (patch.alias !== undefined) {
        setDrafts((d) => { const n = { ...d }; delete n[slug]; return n; });
      }
      setMessage('Gespeichert. Änderungen greifen beim nächsten Login (die Sidebar liest die Einstellungen einmal pro Sitzung).');
    } catch (e) {
      setError(`Speichern fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setSavingSlug(null);
    }
  }

  function saveAlias(slug: string): void {
    const draft = drafts[slug];
    if (draft === undefined) return;
    void patchOverride(slug, { alias: draft.trim() === '' ? null : draft.trim() });
  }
  function toggleHidden(slug: string, currentHidden: boolean): void {
    void patchOverride(slug, { hidden: !currentHidden });
  }
  function saveSection(slug: string, value: string): void {
    void patchOverride(slug, { section: value === '' ? null : value });
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100 mb-2">Sidebar: Beschriftung, Sichtbarkeit &amp; Gruppe</h1>
        <p className="text-slate-400 text-sm max-w-3xl">
          Pro Sidebar-Zeile kannst du einen eigenen Anzeige-Text festlegen, die Zeile
          für alle Nutzer dieses Mandanten ausblenden oder sie einer anderen Gruppe
          zuordnen. Alias leer, sichtbar und Gruppe „Standard" = Vorgabe aus dem Modul.
        </p>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-slate-400 flex gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
        <div>
          <b>Vorrang:</b> Einstellung hier &gt; Modul-Container &gt; Vorgabe im Portal. Das gilt für Text und Gruppe.
          Die Tabelle ist nach der wirksamen Gruppe sortiert — nach dem Umstellen wandert die Zeile in ihre neue Gruppe.
          Änderungen werden beim nächsten Login übernommen.
        </div>
      </div>

      {message && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
          Fehler: {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Sidebar-Einträge ...
        </div>
      ) : (
        SIDEBAR_GROUPS.map((g) => {
          const items = grouped.get(g.slug) ?? [];
          if (items.length === 0) return null;
          const GIcon = g.icon;
          return (
            <section key={g.slug} className="rounded-xl border border-slate-700 overflow-hidden" data-test-anchor={`sidebar-group-${g.slug}`}>
              <div className="bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 flex items-center gap-2">
                <GIcon size={15} className="text-primary" /> {g.label}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium w-44">Kennung</th>
                    <th className="text-left px-3 py-1.5 font-medium">Standard</th>
                    <th className="text-left px-3 py-1.5 font-medium">Alias (Mandant)</th>
                    <th className="text-left px-3 py-1.5 font-medium w-52">Gruppe</th>
                    <th className="text-center px-3 py-1.5 font-medium w-24">Sichtbar</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const ov = overrides[row.slug] ?? NO_OVERRIDE;
                    const draftValue = drafts[row.slug];
                    const currentInput = draftValue !== undefined ? draftValue : (ov.alias ?? '');
                    const dirty = draftValue !== undefined && draftValue !== (ov.alias ?? '');
                    const isSaving = savingSlug === row.slug;
                    const sectionValue = isSidebarGroupSlug(ov.section) ? ov.section : '';
                    return (
                      <tr key={row.slug} className="border-t border-slate-700">
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-400 align-middle">
                          {row.slug}
                          {row.source === 'dynamic' && (
                            <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-primary/20 text-primary uppercase tracking-wider" title="vom Modul-Container registriert">dyn</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 align-middle">{row.defaultLabel}</td>
                        <td className="px-3 py-1.5 align-middle">
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={currentInput}
                              placeholder="(Standard übernehmen)"
                              maxLength={60}
                              disabled={isSaving}
                              onChange={(e) => setDrafts((d) => ({ ...d, [row.slug]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter' && dirty) saveAlias(row.slug); }}
                              className="w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm focus:border-primary outline-none disabled:opacity-50"
                            />
                            {dirty && (
                              <button
                                type="button"
                                onClick={() => saveAlias(row.slug)}
                                disabled={isSaving}
                                className="shrink-0 rounded bg-primary text-primary-foreground px-2 py-1 text-xs hover:bg-primary/90 disabled:opacity-50"
                              >
                                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Speichern'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 align-middle">
                          <select
                            value={sectionValue}
                            disabled={isSaving}
                            onChange={(e) => saveSection(row.slug, e.target.value)}
                            title="Arbeitsbereich-Gruppe in der Sidebar"
                            className="w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm focus:border-primary outline-none disabled:opacity-50"
                          >
                            <option value="">Standard ({groupDef(row.defaultGroup).label})</option>
                            {SIDEBAR_GROUPS.map((opt) => (
                              <option key={opt.slug} value={opt.slug}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-1.5 text-center align-middle">
                          <button
                            type="button"
                            onClick={() => toggleHidden(row.slug, ov.hidden)}
                            disabled={isSaving}
                            title={ov.hidden ? 'Aktuell ausgeblendet — Klick blendet ein' : 'Sichtbar — Klick blendet aus'}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded border transition-colors disabled:opacity-50 ${
                              ov.hidden
                                ? 'border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                                : 'border-slate-700 text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            {ov.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          );
        })
      )}
    </div>
  );
}
